import { LAB_REPORT_NAME_MAP } from '@constants/option-types/lab';

import { isBooleanValue, isNumberValue } from '@utils/lab';

import type { ObservationModelInput } from 'generated/types';

import { FhirResource } from './fhir-resource';
import { ObservationRecord } from './observation';

export type LabObservationValues = Pick<
  LabResultObservationRecord,
  'category' | 'code' | 'displayName' | 'id' | 'testDate' | 'unit' | 'value'
>;

export class LabResultObservationRecord extends ObservationRecord {
  public override get code(): string {
    const coding = this.model.code.coding?.map(({ code }) => code).join(',');
    return coding ?? 'Unknown';
  }

  public override get unit() {
    return this.model.valueQuantity?.unit;
  }

  public set unit(value: string | null | undefined) {
    this.model = {
      ...this.model,
      valueQuantity: {
        unit: value,
        value: typeof this.value === 'number' ? this.value : null,
      },
    };
  }

  public override get value() {
    if (
      this.model.valueBoolean !== undefined &&
      this.model.valueBoolean !== null
    ) {
      // true -> 'Positive'
      // false -> 'Negative'
      return this.model.valueBoolean;
    } else if (
      this.model.valueString !== undefined &&
      this.model.valueString !== null
    ) {
      return this.model.valueString;
    } else if (this.model.valueQuantity) {
      return this.model.valueQuantity.value ?? 'Unknown';
    }
    return 'Unknown';
  }

  public set value(value: boolean | number | string) {
    const numberValue = isNumberValue(value.toString());
    const booleanValue = isBooleanValue(value);

    this.model = {
      ...this.model,
      ...(booleanValue && {
        valueBoolean: value === 'Positive',
      }),
      ...(numberValue && {
        valueQuantity: {
          unit: this.unit,
          value: Number(value),
        },
      }),
      ...(!numberValue &&
        !booleanValue && {
          valueString: value.toString(),
        }),
    };
  }

  // Lab report
  public get category() {
    const [{ coding }] = this.model.category ?? [];
    const [{ code }] = coding ?? [];
    return code ?? undefined;
  }

  public set category(value: string | undefined) {
    this.model = {
      ...this.model,
      category: value
        ? [
            {
              coding: [
                {
                  code: value,
                  display: LAB_REPORT_NAME_MAP[value],
                  system: LabResultObservationRecord.VITALL_SYSTEM,
                },
              ],
              text: LAB_REPORT_NAME_MAP[value],
            },
          ]
        : null,
    };
  }

  public get facility() {
    return this.model.encounterResource?.locationResource
      ?.map(({ name }) => name)
      .join('/');
  }

  // @ts-expect-error LabObservationValues extends ObservationValues and adds fields
  public override getModelInput(
    values?: LabObservationValues
  ): ObservationModelInput {
    if (values) {
      // eslint-disable-next-line guard-for-in
      for (const key in values) {
        try {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          this[key] = values[key];
        } catch {
          // eslint-disable-next-line no-continue
          continue;
        }
      }
      this.setCode(values.code, values.displayName ?? values.code);
    }
    const cleaned = FhirResource.getCleanInput(this.model);
    return cleaned;
  }
}
